/**
 * Backend notifications API — the REAL topbar-bell feed (NOTIF-01).
 *
 * Wraps `GET /v1/notifications` (backend handler
 * `backend/src/handlers/notifications-list.ts`). The server derives a
 * read-only feed of the caller's genuinely-pending actions from existing
 * containers — resolutions awaiting the caller's signature, distributions
 * awaiting their acknowledgement, upcoming trustee meetings, and rejected
 * documents. There is NO fabricated stream: an empty array means there is
 * nothing to do, and the bell shows an honest "all caught up" state.
 *
 * Imported DIRECTLY from this module (NOT re-exported through the api barrel)
 * per the shell batch's import rule.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from './client';

/** The six kinds of pending action — keep in lockstep with the backend
 *  `NotificationKind` union. The client keys its icon + deep-link target off
 *  this. `member-verification-pending` (C3b, multi-member end-client access):
 *  a trust has portal members but nobody's beneficial-ownership FICA
 *  screening has run yet — dedupe id `member-verification-pending:${trustId}`,
 *  carries `trustId`. */
export type NotificationKind =
  | 'awaiting-signature'
  | 'awaiting-acknowledgement'
  | 'upcoming-meeting'
  | 'document-rejected'
  | 'unread-message'
  | 'member-verification-pending';

/** One pending item. PII-clean by contract — see the backend handler header:
 *  `title` is business text (never a beneficiary's name); ids are ids. */
export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  trustId: string;
  trustName: string | null;
  entityId: string;
  /** ISO timestamp for ordering + relative-time rendering. */
  at: string;
  /** True when this item is at-or-before the caller's mark-all-read cursor
   *  (`UserDoc.notificationsReadAt`, written by POST /v1/notifications/read).
   *  Mirrors the backend `NotificationItem.read` (notifications-list.ts). */
  read: boolean;
}

export interface NotificationsListResponse {
  notifications: NotificationItem[];
  /** Items whose sort timestamp is strictly after the caller's mark-all-read
   *  cursor (may exceed `notifications.length` when capped). */
  unreadCount: number;
  /** The caller's mark-all-read cursor (ISO), or null if never cleared. */
  notificationsReadAt: string | null;
  correlationId: string;
}

export interface UseNotificationsOptions {
  /** Restrict to one trust the caller can see. Omit for all visible trusts. */
  trustId?: string;
  /** Soft cap on returned items (backend default 30, max 100). */
  limit?: number;
  /** Defaults to true; set false to defer the call. */
  enabled?: boolean;
}

function buildPath(opts: UseNotificationsOptions): string {
  const params = new URLSearchParams();
  if (opts.trustId) params.set('trustId', opts.trustId);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return qs ? `/api/v1/notifications?${qs}` : '/api/v1/notifications';
}

/**
 * Live notification feed for the topbar bell. Polls on a gentle interval so a
 * freshly-circulated resolution or rejected document shows up without a manual
 * refresh, but not so often it churns the network — the data is "things that
 * need doing", which changes on the order of minutes, not seconds.
 */
export function useNotifications(opts: UseNotificationsOptions = {}) {
  return useQuery<NotificationsListResponse>({
    queryKey: ['notifications', 'list', { trustId: opts.trustId ?? null, limit: opts.limit ?? null }],
    queryFn: ({ signal }) => api<NotificationsListResponse>(buildPath(opts), { signal }),
    enabled: opts.enabled ?? true,
    // 30s freshness + a 60s background refetch (tightened 2026-06-12 per
    // Paul's near-real-time mandate for firm/client surfaces). TanStack also
    // refetches on window focus, so a returning user sees a current bell
    // immediately; true push (SignalR) is a roadmap item, not a poll tweak.
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ── Mark all read (build-campaign 2026-07-09 · lane B · B1) ──────────────────
//
// Wraps `POST /v1/notifications/read` (backend handler
// `backend/src/handlers/notifications-read.ts`). The endpoint takes NO body —
// it stamps the caller's own `UserDoc.notificationsReadAt = now` (a mark-ALL-
// read CURSOR, not a per-item dismiss). The list endpoint reads the cursor
// back to compute `unreadCount` + the per-item `read` flag, so read-state
// survives a fresh session and syncs across devices.

export interface NotificationsReadResponse {
  /** The new mark-all-read cursor (ISO timestamp) just persisted. */
  notificationsReadAt: string;
  correlationId: string;
}

interface MarkReadContext {
  snapshots: [readonly unknown[], NotificationsListResponse | undefined][];
}

/**
 * Mark the caller's whole bell as read. Optimistic: every cached notifications
 * query flips to read (items `read: true`, `unreadCount: 0`) immediately, rolls
 * back on error, and reconciles with the server on settled (refetch reads the
 * cursor-derived state back).
 */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<NotificationsReadResponse, ApiError, void, MarkReadContext>({
    mutationFn: () =>
      api<NotificationsReadResponse>('/api/v1/notifications/read', { method: 'POST' }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['notifications'] });
      const snapshots = qc.getQueriesData<NotificationsListResponse>({
        queryKey: ['notifications'],
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<NotificationsListResponse>(key, {
          ...data,
          unreadCount: 0,
          notifications: data.notifications.map((n) => ({ ...n, read: true })),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
